import { Service, inject } from '@piwikpro/platform';
import { createAction } from 'redux-actions';
import { Confirmation } from '@piwikpro/confirmation-crate';
import { HttpClient } from '@piwikpro/http-crate';
import type { Store } from 'redux';
import {
  NotifyOnFail,
  NotifyAboutUpdate,
  NotifyAboutRemoval,
  NotifyAboutCreation,
  NotifyAboutDelete,
  NotifyAboutAddition,
} from '@piwikpro/notification-crate';
import {
  QueryParams,
  Filters,
} from './model';
import { AddAppsToMetasiteConfirmation } from './components/AddAppsToMetasiteConfirmation';
import { RemoveAppFromMetasiteConfirmation } from './components/RemoveAppFromMetasiteConfirmation';
import { MetaSiteIntegrityResponse, MetaSiteSettingsResponse } from './interfaces';

export const metaSitesFetchInitialized = createAction(
  '@platform/query/metaSites/list/init',
  (payload: any) => payload,
);
export const metaSitesFetchSucceeded = createAction(
  '@platform/query/metaSites/list/success',
  (payload: any) => payload,
);
export const metaSitesFetchFailed = createAction(
  '@platform/query/metaSites/list/failed',
);
export const configureMetaSitesFetch = createAction(
  '@platform/event/metaSites/configureFetch',
);
export const metaSiteCreationInitialized = createAction(
  '@platform/command/metaSites/create/init',
);
export const metaSiteCreationSucceeded = createAction(
  '@platform/command/metaSites/create/success',
);
export const metaSiteCreationFailed = createAction(
  '@platform/command/metaSites/create/failed',
);
export const metaSiteInsideAppsFetchInitialized = createAction(
  '@platform/query/appsInMetaSite/list/init',
);
export const metaSiteInsideAppsFetchSucceeded = createAction(
  '@platform/query/appsInMetaSite/list/success',
  (payload: any) => payload,
);
export const metaSiteInsideAppsFetchFailed = createAction(
  '@platform/query/appsInMetaSite/list/failed',
);
export const metaSiteOutsideAppsFetchInitialized = createAction(
  '@platform/query/appsExcludedFromMetaSite/list/init',
);
export const metaSiteOutsideAppsFetchSucceeded = createAction(
  '@platform/query/appsExcludedFromMetaSite/list/success',
  (payload: any) => payload,
);
export const metaSiteOutsideAppsFetchFailed = createAction(
  '@platform/query/appsExcludedFromMetaSite/list/failed',
);
export const metaSiteAddAppInitialized = createAction(
  '@platform/command/appsInMetaSite/create/init',
);
export const metaSiteAddAppSucceeded = createAction(
  '@platform/command/appsInMetaSite/create/success',
);
export const metaSiteAddAppFailed = createAction(
  '@platform/command/appsInMetaSite/create/failed',
);
export const metaSiteDeleteAppInitialized = createAction(
  '@platform/command/appsInMetaSite/delete/init',
);
export const metaSiteDeleteAppSucceeded = createAction(
  '@platform/command/appsInMetaSite/delete/success',
);
export const metaSiteDeleteAppFailed = createAction(
  '@platform/command/appsInMetaSite/delete/failed',
);
export const metaSiteSettingsFetchInitialized = createAction(
  '@platform/query/metaSites/get/init',
);
export const metaSiteSettingsFetchSucceeded = createAction(
  '@platform/query/metaSites/get/success',
  (payload: any) => payload,
);
export const metaSiteSettingsFetchFailed = createAction(
  '@platform/query/metaSites/get/failed',
);
export const metaSiteSettingsUpdateInitialized = createAction(
  '@platform/command/metaSites/update/init',
);
export const metaSiteSettingsUpdateSucceeded = createAction(
  '@platform/command/metaSites/update/success',
  (payload: any) => payload,
);
export const metaSiteSettingsUpdateFailed = createAction(
  '@platform/command/metaSites/update/failed',
);
export const metaSiteIntegrityFetchInitialized = createAction(
  '@platform/query/metaSites/getIntegrity/init',
);
export const metaSiteIntegrityFetchSucceeded = createAction(
  '@platform/query/metaSites/getIntegrity/success',
  (payload: any) => payload,
);
export const metaSiteIntegrityFetchFailed = createAction(
  '@platform/query/metaSites/getIntegrity/failed',
);

@Service()
export class MetaSites {
  constructor(
    @inject('HttpCrate.httpClient') private readonly httpClient: HttpClient,
    @inject('store') private readonly store: Store,
    @inject('config') private readonly config: any,
    @inject('NotificationCrate.interceptors.NotifyOnFail') private readonly notifyOnFail: NotifyOnFail,
    @inject('NotificationCrate.interceptors.NotifyAboutUpdate') private readonly notifyAboutUpdate: NotifyAboutUpdate,
    @inject('NotificationCrate.interceptors.NotifyAboutCreation') private readonly notifyAboutCreation: NotifyAboutCreation,
    @inject('NotificationCrate.interceptors.NotifyAboutDelete') private readonly notifyAboutDelete: NotifyAboutDelete,
    @inject('NotificationCrate.interceptors.NotifyAboutRemoval') private readonly notfifyAboutRemoval: NotifyAboutRemoval,
    @inject('NotificationCrate.interceptors.NotifyAboutAddition') private readonly notifyAboutAddition: NotifyAboutAddition,
    @inject('ConfirmationCrate.confirmation') private readonly confirmation: Confirmation,
  ) {}

  async fetchMetaSites(
    filters: Filters,
    offset: number = 0,
    limit: number = this.config.get('SIDEBAR_LISTS_ITEMS_PER_PAGE'),
  ) {
    this.store.dispatch(configureMetaSitesFetch(filters));

    if (!offset) {
      this.store.dispatch(metaSitesFetchInitialized({ resetList: true }));
    } else {
      this.store.dispatch(metaSitesFetchInitialized({ resetList: false }));
    }

    const queryParams: QueryParams = {
      sort: filters.sort ? filters.sort.direction + filters.sort.by : 'name',
      offset,
      limit,
      search: filters.search,
      action: 'view_details',
    };

    try {
      const resp = await this.httpClient.request({
        endpoint: this.config.get('META_SITES_URL'),
        queryParams,
        method: 'GET',
      }).intercept(this.notifyOnFail).jsonApi().send();
      this.store.dispatch(metaSitesFetchSucceeded(resp));

      return resp;
    } catch (err) {
      this.store.dispatch(metaSitesFetchFailed());

      throw err;
    }
  }

  async createMetaSite(attributes: { name: string }) {
    this.store.dispatch(metaSiteCreationInitialized());
    try {
      const resp: MetaSiteSettingsResponse = await this.httpClient.request({
        endpoint: this.config.get('META_SITES_URL'),
        method: 'POST',
        body: {
          data: {
            type: 'ppms/meta-site',
            attributes,
          },
        },
      }).intercept(this.notifyAboutCreation).jsonApi().send();

      this.store.dispatch(metaSiteCreationSucceeded(resp));

      return resp;
    } catch (err) {
      this.store.dispatch(metaSiteCreationFailed());

      throw err;
    }
  }

  async fetchAppsInsideMetaSite(
    id: string,
    filters: Filters,
  ) {
    const queryParams = {
      offset: filters.offset,
      limit: filters.limit,
      search: filters.search || '',
      sort: filters.sort ? filters.sort.direction + filters.sort.by : 'name',
    };


    this.store.dispatch(metaSiteInsideAppsFetchInitialized());

    try {
      const resp = await this.httpClient.request({
        endpoint: `${this.config.get('META_SITES_URL')}/${id}/apps`,
        queryParams,
        method: 'GET',
      }).intercept(this.notifyOnFail).jsonApi().send();

      this.store.dispatch(metaSiteInsideAppsFetchSucceeded(resp));

      return resp;
    } catch (err) {
      this.store.dispatch(metaSiteInsideAppsFetchFailed());

      throw err;
    }
  }

  async deleteAppFromMetaSite(
    metaSiteId: string,
    appId: string,
  ) {
    await this.confirmation.confirm({
      messages: [],
      title: 'administration:meta-sites.removeAppFromMetaSiteConfirm.title',
      applyBtnText: 'administration:meta-sites.removeAppFromMetaSiteConfirm.remove-button',
      type: 'customBody',
      asyncAction: async () => {
        this.store.dispatch(metaSiteDeleteAppInitialized());
        try {
          const bodyData = {
            data: [
              {
                type: 'ppms/app',
                id: appId,
              },
            ],
          };

          await this.httpClient.request({
            endpoint: `${this.config.get('META_SITES_URL')}/${metaSiteId}/relationships/apps`,
            method: 'DELETE',
            body: bodyData,
          }).intercept(this.notfifyAboutRemoval).jsonApi().send();

          this.store.dispatch(metaSiteDeleteAppSucceeded());
        } catch (err: any) {
          this.store.dispatch(metaSiteDeleteAppFailed());
          throw err;
        }
      },
    }, RemoveAppFromMetasiteConfirmation);
  }

  async fetchAppsOutsideMetaSite(
    id: string,
    filters: Filters,
  ) {
    const queryParams = {
      offset: filters.offset,
      limit: filters.limit,
      search: filters.search || '',
      sort: filters.sort ? filters.sort.direction + filters.sort.by : 'name',
    };

    this.store.dispatch(metaSiteOutsideAppsFetchInitialized());

    try {
      const resp = await this.httpClient.request({
        endpoint: `${this.config.get('META_SITES_URL')}/${id}/apps/excluded`,
        queryParams,
        method: 'GET',
      }).intercept(this.notifyOnFail).jsonApi().send<any>();

      this.store.dispatch(metaSiteOutsideAppsFetchSucceeded(resp));

      return resp;
    } catch (err) {
      this.store.dispatch(metaSiteOutsideAppsFetchFailed());

      throw err;
    }
  }

  async addAppsToMetaSite(id: string, apps: Array<any>) {
    await this.confirmation.confirm({
      messages: [],
      title: 'administration:meta-sites.addAppsToMetaSiteConfirm.title',
      applyBtnText: 'administration:meta-sites.addAppsToMetaSiteConfirm.add-button',
      applyBtnAppearance: 'primary',
      type: 'customBody',
      asyncAction: async () => {
        this.store.dispatch(metaSiteAddAppInitialized());
        try {
          await this.httpClient.request({
            endpoint: `${this.config.get('META_SITES_URL')}/${id}/relationships/apps`,
            method: 'POST',
            body: {
              data: apps.map(appId => (
                {
                  type: 'ppms/app',
                  id: appId,
                }
              )),
            },
          }).intercept(this.notifyAboutAddition).jsonApi().send();

          this.store.dispatch(metaSiteAddAppSucceeded());
        } catch (err: any) {
          this.store.dispatch(metaSiteAddAppFailed());
          throw err;
        }
      },
    }, AddAppsToMetasiteConfirmation);
  }

  async fetchMetaSiteIntegrity(id: string) {
    this.store.dispatch(metaSiteIntegrityFetchInitialized());

    try {
      const resp: MetaSiteIntegrityResponse = await this.httpClient.request({
        endpoint: `${this.config.get('META_SITES_URL')}/${id}/apps/integrity`,
        method: 'GET',
      }).intercept(this.notifyOnFail).jsonApi().send();

      this.store.dispatch(metaSiteIntegrityFetchSucceeded(resp));

      return resp;
    } catch (err) {
      this.store.dispatch(metaSiteIntegrityFetchFailed());

      throw err;
    }
  }

  async fetchMetaSiteSettings(
    id: string,
    options: {
      skipInitialState?: boolean
    } = {},
  ) {
    if (!options.skipInitialState) {
      this.store.dispatch(metaSiteSettingsFetchInitialized());
    }

    try {
      const resp: MetaSiteSettingsResponse = await this.httpClient.request({
        endpoint: `${this.config.get('META_SITES_URL')}/${id}`,
        method: 'GET',
      }).intercept(this.notifyOnFail).jsonApi().send();

      this.store.dispatch(metaSiteSettingsFetchSucceeded(resp));

      return resp;
    } catch (err) {
      this.store.dispatch(metaSiteSettingsFetchFailed());

      throw err;
    }
  }

  async deleteMetaSite(id: string, metaSiteName: string) {
    await this.confirmation.confirm({
      messages: [
        'administration:meta-sites.deleteConfirm.message-line-1',
        'administration:meta-sites.deleteConfirm.message-line-2',
      ],
      title: 'administration:meta-sites.delete.confirm-title-with-name',
      name: metaSiteName,
      asyncAction: async () => {
        await this.httpClient.request({
          endpoint: `${this.config.get('META_SITES_URL')}/${id}`,
          method: 'DELETE',
        }).intercept(this.notifyAboutDelete).jsonApi().send<any>();
      },
    });
  }

  async updateMetaSite(id: string, attributes: any) {
    this.store.dispatch(metaSiteSettingsUpdateInitialized());

    try {
      await this.httpClient.request({
        endpoint: `${this.config.get('META_SITES_URL')}/${id}`,
        method: 'PATCH',
        body: {
          data: {
            type: 'ppms/meta-site',
            id,
            attributes,
          },
        },
      }).intercept(this.notifyAboutUpdate).jsonApi().send();

      this.store.dispatch(metaSiteSettingsUpdateSucceeded(attributes));

      return attributes;
    } catch (err) {
      this.store.dispatch(metaSiteSettingsUpdateFailed());

      throw err;
    }
  }
}
